'use client';

import * as React from 'react';
import Link from 'next/link';
import { Mail, ArrowLeft, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from '@/components/ui/card';
import { api } from '@/lib/api';
import { useRecaptcha } from '@/lib/hooks/use-recaptcha';
import { useTranslations } from '@/lib/i18n';

export default function ForgotPasswordPage() {
  const t = useTranslations('auth.forgot-password');
  const { getRecaptchaToken } = useRecaptcha();
  const [email, setEmail] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const recaptchaToken = await getRecaptchaToken('forgot_password');

      await api.post('/auth/forgot-password', {
        email,
        recaptchaToken,
      });

      setSuccess(true);
    } catch (err: any) {
      setError(err.message || t('failed-to-send-reset-email-please-try-ag'));
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <Card className="w-full max-w-sm animate-fade-in">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="rounded-full bg-success/20 p-3">
              <CheckCircle className="h-8 w-8 text-success-text" />
            </div>
          </div>
          <CardTitle className="text-2xl">{t('check-your-email')}</CardTitle>
          <CardDescription>
            {t('if-an-account-exists-with')} <span className="font-medium text-foreground">{email}</span>{t('you-ll-receive-a-password-reset-link-sho')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground text-center">
            {t('didn-t-receive-the-email-check-your-spam')}</p>
        </CardContent>
        <CardFooter className="flex-col gap-2">
          <Button asChild variant="outline" className="w-full">
            <Link href="/login">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('back-to-login')}</Link>
          </Button>
        </CardFooter>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-sm animate-fade-in">
      <CardHeader>
        <CardTitle className="text-2xl">{t('forgot-password')}</CardTitle>
        <CardDescription>
          {t('enter-your-email-address-and-we-ll-send')}</CardDescription>
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 text-sm text-error-text bg-destructive/20 border border-destructive/30 rounded-md">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">{t('email')}</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                placeholder={t('you-example-com')}
                className="pl-10"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
                autoComplete="email"
              />
            </div>
          </div>

          <Button type="submit" className="w-full" isLoading={isLoading}>
            {!isLoading && (
              <>
                {t('send-reset-link')}<ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </form>
      </CardContent>

      <CardFooter className="justify-center">
        <Link
          href="/login"
          className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('back-to-login')}</Link>
      </CardFooter>
    </Card>
  );
}
